"use client";

import * as React from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { cn } from "../../lib/cn";
import { FileItem } from "./FileItem";
import { springs } from "../../lib/motion";
import type { UploadFile } from "../../lib/fileUtils";

/**
 * UploadQueue — the live upload list (the donor's `FileUploadList.tsx`).
 *
 * Renders a header ("Attached files"), a scroll-capped column of `FileItem`
 * rows, and animates rows in as files are added / out on
 * remove + complete. The entrance / exit is the same spring-driven
 * `AnimatePresence` pattern `AnimatedList` uses internally.
 *
 * Why not wrap `<AnimatedList>` directly (the issue's suggested base): that
 * component is built for ephemeral notification stacks — it auto-cycles items
 * on a timer (`autoAddDelay` default 2000ms, which would re-inject queue
 * rows), and it wraps every item in its own non-overridable
 * `rounded-2xl border bg-card p-4` card, which double-chromes a `FileItem`
 * that already owns its row surface + status tint. A controlled upload queue
 * needs neither, so we compose the same motion primitive (`AnimatePresence` +
 * `springs.gentle`) at the row level and skip the stack machinery. The visual
 * result — rows slide in, exit on remove — is identical.
 *
 * Reduced-motion: rows appear without the spring entrance (gated on
 * `useReducedMotion()`), matching `AnimatedList`'s own fallback.
 *
 * Controlled: the consumer owns `items` and mutates each row's
 * `status` / `progress` as its transport resolves. Returns `null` when empty.
 *
 * @example
 *   <UploadQueue items={queue} onRetry={retry} onRemove={remove} />
 */
export interface UploadQueueProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** The queue (controlled by the consumer). */
  items: UploadFile[];
  /** Retry handler, forwarded to each errored row. */
  onRetry?: (id: string) => void;
  /** Remove handler, forwarded to each row (shows an X when provided). */
  onRemove?: (id: string) => void;
  /** Header label. Default "Attached files". Pass `null` to hide. */
  title?: React.ReactNode;
  /** Max height of the scroll region. Default `15rem` (the donor's `max-h-60`). */
  maxHeight?: number | string;
  /** Escape hatch: render a custom row instead of `<FileItem>`. */
  renderRow?: (file: UploadFile) => React.ReactNode;
}

export const UploadQueue = React.forwardRef<HTMLDivElement, UploadQueueProps>(
  (
    {
      items,
      onRetry,
      onRemove,
      title = "Attached files",
      maxHeight = "15rem",
      renderRow,
      className,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    if (items.length === 0) return null;

    return (
      <div ref={ref} className={cn("space-y-3", className)} {...rest}>
        {title !== null ? (
          <h4 className="text-sm font-medium text-foreground">{title}</h4>
        ) : null}
        <ul
          className="space-y-2 overflow-y-auto"
          style={{ maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight }}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {items.map((file) => (
              <motion.li
                key={file.id}
                layout={!reduced}
                initial={reduced ? false : { opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8, height: 0 }}
                transition={reduced ? { duration: 0 } : springs.gentle}
                style={{ overflow: "hidden" }}
              >
                {renderRow ? (
                  renderRow(file)
                ) : (
                  <FileItem file={file} onRetry={onRetry} onRemove={onRemove} />
                )}
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </div>
    );
  },
);
UploadQueue.displayName = "UploadQueue";
